import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Upload, FileText, RefreshCw, Trash2, Eye, ChevronDown, ChevronUp, ArrowDownCircle, ArrowUpCircle } from 'lucide-react';
import { BankStatementsService } from '../services/BankStatementsService';
import { SkeletonRows, EmptyGoodNews, ErrorBanner } from './shell/carbonStates';

// CHA-275 — Carbon pass (Modules concept). Presentation only: the upload/OCR
// flows (BankStatementsService Firestore + Storage) are byte-identical; only
// the chrome moved to carbon-* tokens (radius 0, Plex, mono chips/grids).

export default function BankStatementsPanel() {
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  // Scoped per statement (doc id) — a single boolean leaked the toggle from
  // one expanded card to the next (CodeRabbit, #195).
  const [rawTextOpenId, setRawTextOpenId] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    let unsubscribe = () => {};

    const init = async () => {
      try {
        BankStatementsService.setTenant('inmocean');
        await BankStatementsService.initialize();
        unsubscribe = BankStatementsService.subscribeToStatements((docs) => {
          setStatements(docs);
          setLoading(false);
        });
      } catch (err) {
        console.error('Failed to initialize bank statements:', err);
        setLoading(false);
      }
    };

    init();
    return () => unsubscribe();
  }, []);

  const handleUpload = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        await BankStatementsService.uploadStatement(file);
      }
    } catch (err) {
      console.error('Upload failed:', err);
      alert(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
    }
  }, []);

  const handleFileSelect = (e) => {
    handleUpload(Array.from(e.target.files));
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleUpload(Array.from(e.dataTransfer.files));
  };

  const handleDelete = async (docId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this bank statement?')) return;
    try {
      await BankStatementsService.deleteStatement(docId);
    } catch (err) {
      alert(`Delete failed: ${err.message}`);
    }
  };

  const handleReprocess = async (docId, e) => {
    e.stopPropagation();
    try {
      await BankStatementsService.reprocessStatement(docId);
    } catch (err) {
      alert(`Reprocess failed: ${err.message}`);
    }
  };

  const handleViewPdf = async (docId, e) => {
    e.stopPropagation();
    try {
      const url = await BankStatementsService.getViewUrl(docId);
      window.open(url, '_blank');
    } catch (err) {
      alert(`Failed to get URL: ${err.message}`);
    }
  };

  const fmtCur = (v) => {
    if (v == null) return '-';
    const n = Number(v) || 0;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(n);
  };

  const fmtDate = (d) => {
    if (!d) return '-';
    if (d.toDate) d = d.toDate();
    if (typeof d === 'string') d = new Date(d);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Bordered mono status chips mapped to the panel's REAL statuses
  // (processing / processed / error).
  const statusChip = (status) => {
    const map = {
      processing: { color: '#BA4E00', label: 'PROCESSING' },
      processed: { color: '#24A148', label: 'PROCESSED' },
      error: { color: '#DA1E28', label: 'ERROR' }
    };
    const s = map[status] || map.processing;
    return (
      <span className="inline-flex items-center font-plex-mono text-[10px] font-medium border px-1.5 py-0.5" style={{ color: s.color, borderColor: s.color }}>
        {s.label}
      </span>
    );
  };

  const TH = 'px-3 py-2 font-plex-mono text-[10px] tracking-[0.12em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border bg-carbon-gray-10';

  return (
    <div className="font-plex text-carbon-ink space-y-4">
      {/* Upload Zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border border-dashed p-6 text-center cursor-pointer bg-carbon-white ${
          dragOver ? 'border-carbon-blue-60 bg-carbon-blue-10' : 'border-carbon-gray-30 hover:border-carbon-gray-50 hover:bg-carbon-gray-10'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
        {uploading ? (
          <div className="flex items-center justify-center gap-2 text-carbon-blue-60">
            <RefreshCw size={20} className="animate-spin" />
            <span className="text-sm font-medium">Uploading...</span>
          </div>
        ) : (
          <>
            <Upload size={24} className="mx-auto text-carbon-gray-50 mb-2" />
            <p className="text-sm text-carbon-ink font-medium">Drop bank statement files here or click to browse</p>
            <p className="font-plex-mono text-[10px] tracking-[0.12em] uppercase text-carbon-gray-50 mt-1.5">Accepts PDF · JPG · PNG</p>
          </>
        )}
      </div>

      {/* Statement List */}
      {loading ? (
        <div className="bg-carbon-white border border-carbon-border">
          <SkeletonRows rows={3} />
        </div>
      ) : statements.length === 0 ? (
        <div className="bg-carbon-white border border-carbon-border">
          <EmptyGoodNews title="No statements uploaded." detail="Drop a PDF or image above to run OCR on it." />
        </div>
      ) : (
        <div className="space-y-2">
          {statements.map((stmt) => {
            const isExpanded = expandedId === stmt.id;
            const txnCount = stmt.transactionCount || (stmt.transactions ? stmt.transactions.length : 0);
            return (
              <div key={stmt.id} className="bg-carbon-white border border-carbon-border overflow-hidden">
                {/* Card Header — keyboard-operable expand/collapse */}
                <div
                  role="button"
                  tabIndex={0}
                  aria-expanded={isExpanded}
                  onClick={() => setExpandedId(isExpanded ? null : stmt.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setExpandedId(isExpanded ? null : stmt.id);
                    }
                  }}
                  className="px-[18px] py-3 flex items-center justify-between cursor-pointer hover:bg-carbon-gray-10"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText size={18} className="text-carbon-gray-50 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-carbon-ink truncate m-0">{stmt.fileName}</p>
                      <div className="flex items-center gap-2.5 mt-0.5 font-plex-mono text-[10px] text-carbon-gray-50">
                        <span>{fmtDate(stmt.createdAt || stmt.uploadDate)}</span>
                        {stmt.bankName && <span>{stmt.bankName}</span>}
                        {txnCount > 0 && <span>{txnCount} TXNS</span>}
                        {stmt.closingBalance != null && (
                          <span className="font-medium text-carbon-blue-60">CLOSING {fmtCur(stmt.closingBalance)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {statusChip(stmt.status)}
                    {isExpanded ? <ChevronUp size={16} className="text-carbon-gray-50" /> : <ChevronDown size={16} className="text-carbon-gray-50" />}
                  </div>
                </div>

                {/* Expanded Detail */}
                {isExpanded && (
                  <div className="border-t border-carbon-border px-[18px] py-4 space-y-4">
                    {/* Error message — red-rail recoverable pattern; Reprocess below re-runs OCR */}
                    {stmt.error && (
                      <ErrorBanner what="OCR processing failed for this statement." staleNote={stmt.error} />
                    )}

                    {/* Summary strip — per-tile borders */}
                    {stmt.status === 'processed' && (
                      <div className="grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] border-l border-b border-carbon-border">
                        {[
                          { label: 'Opening balance', value: fmtCur(stmt.openingBalance), color: '#161616' },
                          { label: 'Closing balance', value: fmtCur(stmt.closingBalance), color: '#0F62FE' },
                          { label: 'Total debits', value: fmtCur(stmt.totalDebits), color: '#DA1E28' },
                          { label: 'Total credits', value: fmtCur(stmt.totalCredits), color: '#24A148' }
                        ].map((k) => (
                          <div key={k.label} className="bg-carbon-white px-4 py-3 border-r border-t border-carbon-border min-w-0">
                            <div className="font-plex-mono text-[9.5px] tracking-[0.12em] uppercase text-carbon-gray-50">{k.label}</div>
                            <div className="font-plex-mono text-base font-semibold mt-1" style={{ color: k.color }}>{k.value}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Account Info */}
                    {(stmt.accountNumber || stmt.statementPeriod) && (
                      <div className="flex items-center gap-4 font-plex-mono text-[10.5px] text-carbon-gray-50">
                        {stmt.accountNumber && <span>ACCOUNT <span className="text-carbon-ink font-medium">{stmt.accountNumber}</span></span>}
                        {stmt.statementPeriod && <span>PERIOD <span className="text-carbon-ink font-medium">{stmt.statementPeriod}</span></span>}
                      </div>
                    )}

                    {/* Transaction Table */}
                    {stmt.transactions && stmt.transactions.length > 0 && (
                      <div className="overflow-x-auto border border-carbon-border">
                        <table className="w-full text-xs">
                          <thead>
                            <tr>
                              <th className={`${TH} text-left`}>Date</th>
                              <th className={`${TH} text-left`}>Description</th>
                              <th className={`${TH} text-right`}>Amount</th>
                              <th className={`${TH} text-center`}>Type</th>
                            </tr>
                          </thead>
                          <tbody>
                            {stmt.transactions.map((txn, idx) => (
                              <tr key={idx} className="border-b border-carbon-gray-10 hover:bg-carbon-gray-10">
                                <td className="px-3 py-1.5 font-plex-mono text-[11.5px] text-carbon-gray-70 whitespace-nowrap">{txn.date || '-'}</td>
                                <td className="px-3 py-1.5 text-[13px] text-carbon-ink">{txn.description || '-'}</td>
                                <td className="px-3 py-1.5 text-right font-plex-mono text-[11.5px] font-medium">
                                  <span style={{ color: txn.type === 'credit' ? '#24A148' : '#DA1E28' }}>
                                    {fmtCur(txn.amount)}
                                  </span>
                                </td>
                                <td className="px-3 py-1.5 text-center font-plex-mono text-[10px]">
                                  {txn.type === 'credit' ? (
                                    <span className="inline-flex items-center gap-0.5" style={{ color: '#24A148' }}><ArrowDownCircle size={10} /> CR</span>
                                  ) : (
                                    <span className="inline-flex items-center gap-0.5" style={{ color: '#DA1E28' }}><ArrowUpCircle size={10} /> DR</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Raw Text Toggle — scoped to this statement's id */}
                    {stmt.rawText && (
                      <div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setRawTextOpenId(rawTextOpenId === stmt.id ? null : stmt.id);
                          }}
                          className="text-xs font-medium text-carbon-blue-60 hover:text-carbon-blue-70"
                        >
                          {rawTextOpenId === stmt.id ? 'Hide Raw Text' : 'Show Raw Text'}
                        </button>
                        {rawTextOpenId === stmt.id && (
                          <pre className="mt-2 p-3 bg-carbon-gray-10 border border-carbon-border font-plex-mono text-[10px] text-carbon-gray-70 overflow-x-auto max-h-64 whitespace-pre-wrap">
                            {stmt.rawText}
                          </pre>
                        )}
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 pt-3 border-t border-carbon-border">
                      <button
                        onClick={(e) => handleViewPdf(stmt.id, e)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-carbon-ink text-carbon-white hover:bg-carbon-gray-80"
                      >
                        <Eye size={12} /> View PDF
                      </button>
                      <button
                        onClick={(e) => handleReprocess(stmt.id, e)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-carbon-white border border-carbon-gray-30 text-carbon-ink hover:bg-carbon-gray-10"
                      >
                        <RefreshCw size={12} /> Reprocess
                      </button>
                      <button
                        onClick={(e) => handleDelete(stmt.id, e)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-carbon-white border border-carbon-red-60 text-carbon-red-60 hover:bg-carbon-gray-10"
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
